import React from 'react';
import { Plus, X } from 'lucide-react';
import {
  MEDIA_PACKAGE_PRODUCT_TYPE,
  MEDIA_PACKAGE_MAX_EVENTS,
  MEDIA_PACKAGE_QUICK_PICKS,
  clampMediaPackageEvents,
  mediaPackageDescription,
  mediaPackageListPriceDollars,
  isMediaPackageProductType,
} from '../../lib/media';

// InvoiceLineItemsField — Sprint E item 4.2.
// Repeatable rows for the new-invoice form. Each row: description, qty,
// unit_cost (dollars). Live total at the bottom. Caller owns state — passes
// `items` + `onChange(nextItems)`.
//
// Items are kept in dollars on the wire to keep the input UX human; the form
// converts to cents on submit (qty * unit_cost * 100).

function blankRow(idx = 0) {
  return { id: `tmp-${Date.now()}-${idx}`, description: '', qty: 1, unitCostDollars: 0 };
}

// MP.7 — the media package line item. Adding it makes the invoice's sign hook
// (onInvoiceSigned) auto-create an active media_package for the school with
// exactly the event count on the row, which enables their booking calendar +
// delivery. One product, variable count: events ride in product_metadata and
// the description is regenerated from them so the PDF always says exactly
// what was sold. Price defaults to list and stays editable.
const isMediaPackage = (r) => isMediaPackageProductType(r?.product_type);
const isEditableMediaPackage = (r) => r?.product_type === MEDIA_PACKAGE_PRODUCT_TYPE || r?.product_type === 'media_package_custom';

function rowAmountCents(r) {
  const cents = Math.round((Number(r.qty) || 0) * (Number(r.unitCostDollars) || 0) * 100);
  return Number.isFinite(cents) ? cents : 0;
}

function formatMoney(cents) {
  return (Number(cents) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export default function InvoiceLineItemsField({ items, onChange, disabled = false }) {
  const rows = Array.isArray(items) && items.length > 0 ? items : [blankRow(0)];

  const update = (idx, patch) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
  };

  const addRow = () => onChange([...rows, blankRow(rows.length)]);

  const hasMediaPackage = rows.some(isMediaPackage);

  const addMediaPackage = (events = MEDIA_PACKAGE_QUICK_PICKS[0]) => {
    // Drop any empty placeholder rows so the package lands cleanly.
    const meaningful = rows.filter((r) => (r.description && r.description.trim()) || isMediaPackage(r));
    const meta = { events: clampMediaPackageEvents(events) };
    onChange([
      ...meaningful,
      {
        id: `tmp-${Date.now()}-mp`,
        description: mediaPackageDescription(meta.events),
        qty: 1,
        unitCostDollars: mediaPackageListPriceDollars(meta.events),
        product_type: MEDIA_PACKAGE_PRODUCT_TYPE,
        product_metadata: meta,
      },
    ]);
  };

  // Changing the count regenerates the description and re-fills list price;
  // the admin can then override the unit cost for a quoted/complimentary deal.
  const updateMediaEvents = (idx, value) => {
    const events = clampMediaPackageEvents(value);
    update(idx, {
      product_metadata: { events },
      description: mediaPackageDescription(events),
      unitCostDollars: mediaPackageListPriceDollars(events),
    });
  };

  const removeRow = (idx) => {
    if (rows.length === 1) {
      // Always keep at least one row.
      onChange([blankRow(0)]);
      return;
    }
    onChange(rows.filter((_, i) => i !== idx));
  };

  const total = rows.reduce((sum, r) => sum + rowAmountCents(r), 0);

  return (
    <div data-testid="invoice-line-items-field" className="space-y-2">
      <div className="text-[11px] uppercase tracking-wider text-gray-400/80">Line items</div>
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.04] overflow-x-auto">
        <div className="min-w-[540px] grid grid-cols-12 gap-2 px-3 py-2 text-[10px] uppercase tracking-wider text-gray-500">
          <div className="col-span-7">Description</div>
          <div className="col-span-1 text-right">Qty</div>
          <div className="col-span-2 text-right">Unit cost</div>
          <div className="col-span-1 text-right">Amount</div>
          <div className="col-span-1" />
        </div>
        {rows.map((r, idx) => (
          <div key={r.id} data-testid={`line-item-row-${idx}`} className="min-w-[540px] grid grid-cols-12 gap-2 px-3 py-2 items-center">
            <div className="col-span-7">
              <input
                type="text"
                data-testid={`line-item-description-${idx}`}
                disabled={disabled}
                value={r.description}
                onChange={(e) => update(idx, { description: e.target.value })}
                placeholder="Website Design & Development"
                className="w-full px-2 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white"
              />
              {isMediaPackage(r) && (
                <span data-testid={`line-item-media-badge-${idx}`}
                  className="inline-flex items-center gap-1 mt-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#B0FC38]/[0.12] border border-[#B0FC38]/30 text-[#B0FC38]">
                  Media package · activates booking on sign
                </span>
              )}
            </div>
            <input
              type="number"
              data-testid={`line-item-qty-${idx}`}
              disabled={disabled}
              min="1"
              value={r.qty}
              onChange={(e) => update(idx, { qty: e.target.value })}
              className="col-span-1 px-2 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white text-right"
            />
            <input
              type="number"
              data-testid={`line-item-unit-cost-${idx}`}
              disabled={disabled}
              min="0"
              step="0.01"
              value={r.unitCostDollars}
              onChange={(e) => update(idx, { unitCostDollars: e.target.value })}
              className="col-span-2 px-2 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white text-right"
            />
            <div className="col-span-1 text-right text-[13px] text-white" data-testid={`line-item-amount-${idx}`}>
              {formatMoney(rowAmountCents(r))}
            </div>
            <button
              type="button"
              data-testid={`line-item-remove-${idx}`}
              disabled={disabled}
              onClick={() => removeRow(idx)}
              className="col-span-1 p-1 rounded text-gray-500 hover:text-red-300"
              title="Remove row"
            >
              <X className="w-3.5 h-3.5" />
            </button>
            {isEditableMediaPackage(r) && (
              <div className="col-span-12 flex items-center gap-3 px-1 pb-1 text-[11px] text-gray-400 flex-wrap">
                <label className="inline-flex items-center gap-1.5">
                  Events
                  <input
                    type="number"
                    min={1}
                    max={MEDIA_PACKAGE_MAX_EVENTS}
                    data-testid={`line-item-media-events-${idx}`}
                    disabled={disabled}
                    value={r.product_metadata?.events ?? 1}
                    onChange={(e) => updateMediaEvents(idx, e.target.value)}
                    className="w-14 px-2 py-1 rounded bg-white/[0.04] border border-white/[0.08] text-white text-[12px]"
                  />
                </label>
                <span className="text-gray-500">List price fills in automatically — override the unit cost above for a quoted or complimentary ($0) deal.</span>
              </div>
            )}
          </div>
        ))}
        <div className="flex items-center justify-between px-3 py-2 text-[12px] gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              data-testid="line-item-add"
              disabled={disabled}
              onClick={addRow}
              className="inline-flex items-center gap-1 text-gray-400 hover:text-white"
            >
              <Plus className="w-3 h-3" /> Add row
            </button>
            {hasMediaPackage ? (
              <span className="text-[10px] uppercase tracking-wider text-[#B0FC38]/70">Media package added · activates on sign</span>
            ) : (
              <span className="inline-flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider text-gray-500">Media package:</span>
                {MEDIA_PACKAGE_QUICK_PICKS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`add-media-package-${n}`}
                    disabled={disabled}
                    onClick={() => addMediaPackage(n)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-[#B0FC38]/30 bg-[#B0FC38]/[0.06] text-[11px] text-[#B0FC38] hover:bg-[#B0FC38]/[0.12]"
                  >
                    <Plus className="w-3 h-3" /> {n} events · ${mediaPackageListPriceDollars(n).toLocaleString()}
                  </button>
                ))}
                <button
                  type="button"
                  data-testid="add-media-package-other"
                  disabled={disabled}
                  onClick={() => addMediaPackage(1)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-white/[0.15] bg-white/[0.03] text-[11px] text-gray-200 hover:bg-white/[0.08]"
                >
                  <Plus className="w-3 h-3" /> Other count
                </button>
              </span>
            )}
          </div>
          <div className="text-[13px] text-white">
            <span className="text-[10px] uppercase tracking-wider text-gray-500 mr-2">Total</span>
            <span data-testid="line-items-total">{formatMoney(total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function lineItemsToWire(items) {
  return (items || []).map((r, idx) => ({
    description: r.description?.trim() || '',
    qty: parseInt(r.qty, 10) || 0,
    unit_cost_cents: Math.round((Number(r.unitCostDollars) || 0) * 100),
    display_order: idx,
    // MP.7 — carry the media-package product_type to the backend so the invoice
    // sign hook can auto-create the package. Free-text rows stay null.
    product_type: r.product_type || null,
    // Media packages carry their event count so the sign hook (or an admin
    // grant) knows exactly how many shoots were sold. Other rows keep the
    // historical wire shape (no metadata key at all).
    ...(isEditableMediaPackage(r) && r.product_metadata
      ? { product_metadata_json: JSON.stringify({ events: clampMediaPackageEvents(r.product_metadata.events) }) }
      : {}),
  }));
}
